import {speechHandleRange} from './speechHandles.js';

// A waveform gives evidence about quiet gaps, not definitive phoneme timing.
// Existing speech handles remain the safe baseline if the audio is inconclusive.
export const WAVEFORM_BOUNDARY_VERSION='waveform-gap-v1';
const MIN_QUIET_SECONDS=.0375;
const MIN_LEAD=.10,MIN_TRAIL=.12;
const MAX_LEAD=.45,MAX_TRAIL=.50;
const MAX_OUTWARD_CHANGE=.12;
const EPS=.000001;

function quantile(values,fraction){
 if(!values.length)return 0;
 const sorted=values.slice().sort((a,b)=>a-b);
 return sorted[Math.floor((sorted.length-1)*fraction)];
}
function valuesWithin(wave,start,end){
 if(!wave?.peaks||!Number.isFinite(wave.offset)||!Number.isFinite(wave.rate)||wave.rate<=0
  ||end<=start)return [];
 const first=Math.max(0,Math.ceil((start-wave.offset)*wave.rate-.5));
 const last=Math.min(wave.peaks.length,Math.ceil((end-wave.offset)*wave.rate-.5));
 const values=[];
 for(let i=first;i<last;i++){
  const value=wave.peaks[i];
  if(!Number.isFinite(value)||value<0)return [];
  values.push(value);
 }
 return values;
}
function quietCandidate(wave,edge,raw,baseline,min,max,wordStart,wordEnd){
 if(!wave?.peaks?.length||max-min<MIN_QUIET_SECONDS)return null;
 // Compare with the actual selected word, not a loud neighbouring interviewee.
 const insideStart=edge==='start'?raw+.015:Math.max(wordStart+.01,raw-.24);
 const insideEnd=edge==='start'?Math.min(wordEnd-.01,raw+.24):raw-.015;
 const speech=valuesWithin(wave,insideStart,insideEnd);
 const reference=quantile(speech,.80);
 if(speech.length<3||reference<.025)return null; // quiet / uncertain input
 const threshold=Math.min(.07,Math.max(.007,reference*.18));
 const first=Math.max(0,Math.ceil((min-wave.offset)*wave.rate));
 const last=Math.min(wave.peaks.length,Math.floor((max-wave.offset)*wave.rate));
 const minimumBins=Math.ceil(MIN_QUIET_SECONDS*wave.rate);
 let run=-1,best=null;
 const accept=(a,b)=>{
  if(a<0||b-a<minimumBins)return;
  const time=wave.offset+(a+b)/(2*wave.rate);
  if(time<min-EPS||time>max+EPS)return;
  if(!best||Math.abs(time-baseline)<Math.abs(best-baseline))best=time;
 };
 for(let i=first;i<=last;i++){
  const value=i<last?wave.peaks[i]:Infinity;
  const quiet=Number.isFinite(value)&&value>=0&&value<=threshold;
  if(quiet&&run<0)run=i;
  if(!quiet&&run>=0){accept(run,i);run=-1;}
 }
 return best;
}
// startWave / endWave come from short source-timecode windows, not whole files.
export function waveformSpeechRange(words,range,waves,duration){
 const safe=speechHandleRange(words,range,duration);
 const report={version:WAVEFORM_BOUNDARY_VERSION,start:'safe-padding',end:'safe-padding'};
 if(!Array.isArray(words)||!Number.isFinite(duration)||!Number.isFinite(range?.start)
   ||!Number.isFinite(range?.end)||range.end<=range.start||range.end>duration||range.start<0)
  return {...safe,report};
 const first=words.findIndex(word=>word.start===range.start&&word.end>word.start);
 const last=words.findLastIndex(word=>word.end===range.end&&word.end>word.start);
 if(first<0||last<first)return {...safe,report};
 const before=words[first-1],after=words[last+1];
 const earliest=Number.isFinite(before?.end)?Math.min(range.start,Math.max(0,before.end)):0;
 const latest=Number.isFinite(after?.start)?Math.max(range.end,Math.min(duration,after.start)):duration;
 const firstWord=words[first],lastWord=words[last];
 const startMin=Math.max(earliest,range.start-MAX_LEAD,safe.start-MAX_OUTWARD_CHANGE);
 // A quiet consonant can appear like silence: never shorten safe handles.
 // Waveform evidence may only extend into a clear gap.
 const startMax=Math.min(range.start-MIN_LEAD,safe.start);
 const endMin=Math.max(range.end+MIN_TRAIL,safe.end);
 const endMax=Math.min(latest,range.end+MAX_TRAIL,safe.end+MAX_OUTWARD_CHANGE);
 const inCandidate=quietCandidate(waves?.start,'start',range.start,safe.start,
   startMin,startMax,firstWord.start,firstWord.end);
 const outCandidate=quietCandidate(waves?.end,'end',range.end,safe.end,
   endMin,endMax,lastWord.start,lastWord.end);
 const start=inCandidate===null?safe.start:Math.max(earliest,Math.min(safe.start,inCandidate));
 const end=outCandidate===null?safe.end:Math.min(latest,Math.max(safe.end,outCandidate));
 if(Math.abs(start-safe.start)>.008)report.start='waveform-gap';
 if(Math.abs(end-safe.end)>.008)report.end='waveform-gap';
 return {start,end,report};
}
